import React, { useState, useEffect, useCallback, useRef } from 'react';
import CopilotWidget from '../components/CopilotWidget';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import { spatialApi, type SpatialEvent, type SpatialEventStats, type ProcessedGeometry, type FarmVersion, type DigitalTwinProfile } from '../services/spatialApi';
import LogoutButton from '../components/LogoutButton';
import {
  BrandMark, Card, SectionHeader, MetricTile, StatusPill, SmartStatus,
  Spinner, EmptyState, Alert, IconButton, SegmentedTabs, Avatar, Pagination, usePagination,
} from '../components/ui';

// ─── Inline enterprise icons ───
const Icon = {
  Activity: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>),
  MapPin: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>),
  Clock: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>),
  Refresh: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>),
  Layers: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>),
  Database: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>),
  Cpu: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /><path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3" /></svg>),
  Play: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M5 3l14 9-14 9V3z" /></svg>),
  Sparkles: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 3l1.5 5L19 9.5 13.5 11 12 16l-1.5-5L5 9.5 10.5 8 12 3z" /></svg>),
  Users: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 11-8 0 4 4 0 018 0zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>),
  Cloud: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" /></svg>),
  Leaf: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M11 20A7 7 0 019.8 6.1C15.5 5 17 4.48 19.5 2c-.5 4.5-.5 7.5-3 12-2 3.5-7 4-7 4zM2 21c0-3 1.85-5.36 5.08-6" /></svg>),
  Plus: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 5v14M5 12h14" /></svg>),
};

const TWIN_LEVELS = [
  { level: 0, label: 'No data', icon: '○' },
  { level: 1, label: 'Boundary', icon: '◇' },
  { level: 2, label: 'Crops', icon: '◈' },
  { level: 3, label: 'Weather', icon: '☁' },
  { level: 4, label: 'Satellite', icon: '▣' },
  { level: 5, label: 'IoT', icon: '◉' },
  { level: 6, label: 'AI', icon: '✦' },
  { level: 7, label: 'Predictive', icon: '★' },
];

const SpatialIntelligence: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'events' | 'analytics' | 'geofences' | 'ai-insights' | 'geometry' | 'temporal' | 'twin'>('overview');
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState<any>(null);
  const [overviewStats, setOverviewStats] = useState<any>(null);

  const fetchHealth = useCallback(async () => {
    try {
      const h = await spatialApi.health();
      setHealth(h);
    } catch { /* ignore — DB may not be migrated yet */ }
    try {
      const ovRes = await apiClient.get('/spatial/overview');
      setOverviewStats(ovRes.data);
    } catch { /* non-fatal */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  if (loading) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <BrandMark size="lg" accent="analytics" />
          <div className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
            <Spinner size="sm" /> Initializing Spatial OS…
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview',    label: 'Overview',      icon: <Icon.Activity className="w-4 h-4" /> },
    { id: 'events',      label: 'Event Stream',  icon: <Icon.Activity className="w-4 h-4" /> },
    { id: 'analytics',   label: 'Analytics',     icon: <Icon.Layers className="w-4 h-4" /> },
    { id: 'geofences',   label: 'Geofences',     icon: <Icon.MapPin className="w-4 h-4" /> },
    { id: 'ai-insights', label: 'AI Insights',   icon: <Icon.Sparkles className="w-4 h-4" /> },
    { id: 'geometry',    label: 'Geometry Lab',  icon: <Icon.MapPin className="w-4 h-4" /> },
    { id: 'temporal',    label: 'Temporal',      icon: <Icon.Clock className="w-4 h-4" /> },
    { id: 'twin',        label: 'Digital Twin',  icon: <Icon.Cpu className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 pb-24 md:pb-0">
      {/* Top bar */}
      <header className="topbar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <BrandMark size="md" accent="analytics" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate flex items-center gap-2">
                Spatial Intelligence
                <span className="hidden md:inline-flex pill-success">
                  <span className="live-dot" /> {health?.subscribers || 0} subscribers
                </span>
              </p>
              <p className="text-xs text-ink-500 dark:text-ink-400 truncate">
                Spatial OS · {health?.sseClients || 0} live clients · {health?.eventTypes?.length || 11} event types
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <IconButton label="Refresh" onClick={fetchHealth}>
              <Icon.Refresh className="w-4 h-4" />
            </IconButton>
            <LogoutButton />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 pt-6 pb-6">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <MetricTile label="Subscribers"     value={health?.subscribers || 0}              icon={<Icon.Activity className="w-4 h-4" />} accent="analytics" />
          <MetricTile label="SSE clients"     value={health?.sseClients || 0}              icon={<Icon.Layers className="w-4 h-4" />} />
          <MetricTile label="Event types"     value={health?.eventTypes?.length || 0}      icon={<Icon.Database className="w-4 h-4" />} />
          <MetricTile label="Status"          value="Operational"                          icon={<Icon.Cpu className="w-4 h-4" />} />
        </div>

        {/* Tabs */}
        <div className="hidden md:block mb-6">
          <SegmentedTabs items={tabs} active={activeTab} onChange={(id) => setActiveTab(id as any)} />
        </div>
        <div className="md:hidden mb-5 -mx-4 px-4 overflow-x-auto no-scrollbar">
          <div className="inline-flex gap-1.5 pb-1">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id as any)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  activeTab === t.id ? 'bg-ink-900 dark:bg-ink-50 text-ink-50 dark:text-ink-900' : 'bg-ink-0 dark:bg-ink-850 text-ink-600 dark:text-ink-300 border border-ink-200 dark:border-ink-800'
                }`}>
                {t.icon}<span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ═══ OVERVIEW ═══ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricTile label="Mapped farms" value={overviewStats?.mapped_farms ?? 0} icon={<Icon.MapPin className="w-4 h-4" />} accent="admin" />
              <MetricTile label="Live trucks" value={overviewStats?.live_trucks ?? 0} icon={<Icon.Activity className="w-4 h-4" />} />
              <MetricTile label="Active geofences" value={overviewStats?.active_geofences ?? 0} hint={`${overviewStats?.farm_geofences ?? 0} farm boundaries`} icon={<Icon.Layers className="w-4 h-4" />} />
              <MetricTile label="Spatial layers" value={overviewStats?.active_layers ?? 0} icon={<Icon.Layers className="w-4 h-4" />} />
              <MetricTile label="Geofence events (24h)" value={overviewStats?.geofence_events_24h ?? 0} hint={`${overviewStats?.geofence_events_total ?? 0} total`} icon={<Icon.Clock className="w-4 h-4" />} />
              <MetricTile label="AI insights (active)" value={overviewStats?.ai_insights_active ?? 0} hint={`${overviewStats?.ai_insights_critical ?? 0} critical`} icon={<Icon.Sparkles className="w-4 h-4" />} />
              <MetricTile label="Subscribers" value={health?.subscribers ?? 0} hint={`${health?.sseClients ?? 0} SSE clients`} icon={<Icon.Cpu className="w-4 h-4" />} />
              <MetricTile label="Event types" value={health?.eventTypes?.length ?? 11} icon={<Icon.Database className="w-4 h-4" />} />
            </div>

            {/* System architecture diagram */}
            <Card padding="lg">
              <SectionHeader eyebrow="Architecture" title="Spatial Operating System v2" subtitle="In-process event bus + PostGIS + AI insights + geofence detection" icon={<Icon.Cpu className="w-4 h-4" />} />
              <div className="mt-4 p-4 rounded-lg surface-inset font-mono text-2xs leading-relaxed overflow-x-auto">
{`MACRO INTELLIGENCE FOUNDATION
  Cameroon (national) → Region → Village → Farm (polygon) → GPS Intelligence → Weather → AI Diagnosis

SPATIAL EVENT BUS (11 event types)
  GPS_POINT_RECEIVED → TRACK_UPDATED → POLYGON_CREATED/UPDATED → AREA_CHANGED
  → FIELD_CREATED → BOUNDARY_APPROVED → CROP_PLANTED/HARVESTED → WEATHER_SNAPSHOT → SYNC_COMPLETED → TWIN_LEVEL_CHANGED

ENTERPRISE v2 LAYERS
  1. Spatial Layers Registry (10 pre-registered: farms, trucks, weather, risk, villages, EUDR, agents)
  2. Heatmaps (disease risk, farm density, truck density, yield)
  3. Spatial Clustering (grid-based grouping)
  4. Spatial Joins (farms within X meters of a point/road/truck)
  5. Geofences (auto-created per farm + custom perimeters)
  6. Geofence Event Detection (enter/exit/dwell)
  7. AI Spatial Insights (disease hotspots, yield predictions via Gemini)
  8. Spatial Query Cache (15-min TTL, configurable)

DIGITAL TWIN (7-level maturity model)
  L0 No data → L1 Boundary → L2 Crops → L3 Weather → L4 Satellite → L5 IoT → L6 AI → L7 Predictive`}
              </div>
            </Card>

            {/* Quick actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card padding="lg" className="cursor-pointer hover:ring-2 hover:ring-admin-500/50 transition-all" >
                <div onClick={() => setActiveTab('analytics')}>
                  <Icon.Layers className="w-6 h-6 text-admin-600 dark:text-admin-400 mb-2" />
                  <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">Spatial Analytics</p>
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1">Heatmaps, clusters, density, spatial joins</p>
                </div>
              </Card>
              <Card padding="lg" className="cursor-pointer hover:ring-2 hover:ring-admin-500/50 transition-all">
                <div onClick={() => setActiveTab('geofences')}>
                  <Icon.MapPin className="w-6 h-6 text-admin-600 dark:text-admin-400 mb-2" />
                  <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">Geofences</p>
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1">Virtual perimeters + enter/exit detection</p>
                </div>
              </Card>
              <Card padding="lg" className="cursor-pointer hover:ring-2 hover:ring-admin-500/50 transition-all">
                <div onClick={() => setActiveTab('ai-insights')}>
                  <Icon.Sparkles className="w-6 h-6 text-admin-600 dark:text-admin-400 mb-2" />
                  <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">AI Insights</p>
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1">Disease hotspots, yield predictions via Gemini</p>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'events' && <EventStreamTab />}
        {activeTab === 'analytics' && <SpatialAnalyticsTab />}
        {activeTab === 'geofences' && <GeofencesTab />}
        {activeTab === 'ai-insights' && <AiInsightsTab />}
        {activeTab === 'geometry' && <GeometryLabTab />}
        {activeTab === 'temporal' && <TemporalExplorerTab />}
        {activeTab === 'twin' && <DigitalTwinTab />}
      </main>

      {/* Mobile bottom nav */}
      <nav className="bottomnav md:hidden">
        <div className="grid grid-cols-4">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id as any)}
              className={`relative flex flex-col items-center justify-center gap-1 py-2.5 text-2xs font-medium transition-colors ${
                activeTab === t.id ? 'text-ink-900 dark:text-ink-50' : 'text-ink-500 dark:text-ink-400'
              }`}>
              {t.icon}<span>{t.label}</span>
              {activeTab === t.id && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-analytics-700 rounded-full" />}
            </button>
          ))}
        </div>
      </nav>
          <CopilotWidget userRole={(user as any)?.role || 'admin'} />
</div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   EVENT STREAM TAB — live SSE feed + stats
   ═══════════════════════════════════════════════════════════════ */
const EventStreamTab: React.FC = () => {
  const [events, setEvents] = useState<SpatialEvent[]>([]);
  const [stats, setStats] = useState<SpatialEventStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const eventsPag = usePagination(events, 25);

  const fetchInitial = useCallback(async () => {
    try {
      const [evs, st] = await Promise.all([
        spatialApi.getEvents(100),
        spatialApi.getEventStats().catch(() => null),
      ]);
      setEvents(evs);
      setStats(st);
    } catch { /* DB may not be migrated */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchInitial();
    // SSE connection
    const cleanup = spatialApi.streamEvents(
      (event) => {
        setConnected(true);
        setEvents(prev => [event, ...prev].slice(0, 200));
      },
      () => setConnected(false),
    );
    cleanupRef.current = cleanup;
    return () => { cleanup(); };
  }, [fetchInitial]);

  const eventTypeTone = (type: string): 'success' | 'info' | 'warning' | 'neutral' | 'danger' => {
    if (type.includes('CREATED') || type.includes('APPROVED') || type.includes('COMPLETED')) return 'success';
    if (type.includes('UPDATED') || type.includes('CHANGED')) return 'info';
    if (type.includes('REJECTED') || type.includes('ALERT')) return 'warning';
    if (type.includes('FAILED')) return 'danger';
    return 'neutral';
  };

  if (loading) {
    return <Card padding="lg"><div className="flex justify-center py-8"><Spinner /></div></Card>;
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricTile label="Total events"     value={stats.total}      icon={<Icon.Database className="w-4 h-4" />} accent="analytics" />
          <MetricTile label="Last 24 hours"    value={stats.last24h}    icon={<Icon.Clock className="w-4 h-4" />} />
          <MetricTile label="Event types"      value={Object.keys(stats.byType).length} icon={<Icon.Layers className="w-4 h-4" />} />
          <MetricTile label="SSE connection"   value={connected ? 'Live' : 'Disconnected'} icon={<Icon.Activity className="w-4 h-4" />} />
        </div>
      )}

      {/* By-type breakdown */}
      {stats && Object.keys(stats.byType).length > 0 && (
        <Card padding="lg">
          <SectionHeader eyebrow="Distribution" title="Events by type" icon={<Icon.Layers className="w-4 h-4" />} />
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.entries(stats.byType)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => (
                <div key={type} className="flex items-center justify-between p-2.5 surface-inset rounded-lg">
                  <StatusPill tone={eventTypeTone(type)} dot>{type.replace(/_/g, ' ')}</StatusPill>
                  <span className="text-sm font-semibold nums text-ink-900 dark:text-ink-50">{count}</span>
                </div>
              ))}
          </div>
        </Card>
      )}

      {/* Live feed */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex items-center justify-between">
          <SectionHeader
            eyebrow="Live"
            title="Event stream"
            subtitle={connected ? 'Receiving events in real time via SSE' : 'Showing cached events (SSE disconnected)'}
            icon={<Icon.Activity className="w-4 h-4" />}
          />
          {connected && <span className="live-dot" />}
        </div>
        {eventsPag.total === 0 ? (
          <EmptyState
            icon={<Icon.Activity className="w-5 h-5" />}
            title="No events yet"
            description="Spatial events will appear here as GPS pings, polygon captures, and other spatial operations occur."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Time</th><th>Type</th><th>Entity</th><th>Farmer</th><th>Payload</th></tr></thead>
              <tbody>
                {eventsPag.paginatedItems.map((e) => (
                  <tr key={e.id}>
                    <td className="text-2xs text-ink-500 dark:text-ink-400 nums whitespace-nowrap">
                      {new Date(e.recorded_at).toLocaleTimeString()}
                    </td>
                    <td><StatusPill tone={eventTypeTone(e.event_type)} dot>{e.event_type.replace(/_/g, ' ')}</StatusPill></td>
                    <td className="text-2xs">
                      <span className="text-ink-500 dark:text-ink-400">{e.entity_type}</span>
                      <span className="font-mono text-ink-400 dark:text-ink-500 ml-1">#{e.entity_id?.slice(0, 8)}</span>
                    </td>
                    <td className="font-mono text-2xs text-ink-500 dark:text-ink-400">
                      {e.farmer_id ? e.farmer_id.slice(0, 8) + '…' : '—'}
                    </td>
                    <td className="text-2xs text-ink-500 dark:text-ink-400 font-mono max-w-xs truncate">
                      {JSON.stringify(e.payload).slice(0, 80)}
                      {JSON.stringify(e.payload).length > 80 ? '…' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {eventsPag.total > 0 && (
          <div className="border-t border-ink-200 dark:border-ink-800">
            <Pagination page={eventsPag.page} pageSize={eventsPag.pageSize} total={eventsPag.total} onPageChange={eventsPag.setPage} />
          </div>
        )}
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   GEOMETRY LAB TAB — interactive pipeline tester
   ═══════════════════════════════════════════════════════════════ */
const GeometryLabTab: React.FC = () => {
  const [inputText, setInputText] = useState(`[
  { "lat": 4.0511, "lng": 9.7679, "accuracy": 5 },
  { "lat": 4.0512, "lng": 9.7680, "accuracy": 8 },
  { "lat": 4.0515, "lng": 9.7685, "accuracy": 4 },
  { "lat": 4.0520, "lng": 9.7690, "accuracy": 6 },
  { "lat": 4.0525, "lng": 9.7685, "accuracy": 5 },
  { "lat": 4.0528, "lng": 9.7679, "accuracy": 12 },
  { "lat": 4.0525, "lng": 9.7670, "accuracy": 7 },
  { "lat": 4.0520, "lng": 9.7668, "accuracy": 4 },
  { "lat": 4.0515, "lng": 9.7672, "accuracy": 5 },
  { "lat": 4.0511, "lng": 9.7679, "accuracy": 5 }
]`);
  const [result, setResult] = useState<ProcessedGeometry | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleProcess = async () => {
    setError(null);
    setResult(null);
    let points: any[];
    try {
      points = JSON.parse(inputText);
      if (!Array.isArray(points) || points.length < 3) throw new Error('Need at least 3 points');
    } catch (err: any) {
      setError(err.message || 'Invalid JSON');
      return;
    }
    setProcessing(true);
    try {
      const r = await spatialApi.processGeometry(points);
      setResult(r);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Pipeline failed');
    } finally {
      setProcessing(false);
    }
  };

  const loadSample = (kind: 'square' | 'noisy' | 'sparse') => {
    const samples = {
      square: [
        { lat: 4.0511, lng: 9.7679 },
        { lat: 4.0521, lng: 9.7679 },
        { lat: 4.0521, lng: 9.7689 },
        { lat: 4.0511, lng: 9.7689 },
        { lat: 4.0511, lng: 9.7679 },
      ],
      noisy: Array.from({ length: 30 }, (_, i) => ({
        lat: 4.0511 + 0.001 * Math.sin(i * 0.4) + (Math.random() - 0.5) * 0.00005,
        lng: 9.7679 + 0.001 * Math.cos(i * 0.4) + (Math.random() - 0.5) * 0.00005,
        accuracy: 3 + Math.random() * 15,
      })),
      sparse: [
        { lat: 4.0511, lng: 9.7679 },
        { lat: 4.0515, lng: 9.7690 },
        { lat: 4.0525, lng: 9.7685 },
      ],
    };
    setInputText(JSON.stringify(samples[kind], null, 2));
    setResult(null);
    setError(null);
  };

  return (
    <div className="space-y-6">
      <Card padding="lg">
        <SectionHeader
          eyebrow="Pipeline"
          title="Geometry processing lab"
          subtitle="Run raw GPS tracks through the deterministic geometry pipeline. Each stage is independently measurable."
          icon={<Icon.MapPin className="w-4 h-4" />}
        />
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => loadSample('square')} className="btn-secondary text-xs">Load square sample</button>
          <button onClick={() => loadSample('noisy')} className="btn-secondary text-xs">Load noisy circle (30 pts)</button>
          <button onClick={() => loadSample('sparse')} className="btn-secondary text-xs">Load sparse (3 pts)</button>
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="label">Raw GPS points (JSON array)</label>
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              rows={14}
              className="input font-mono text-xs resize-none"
              placeholder='[{ "lat": 4.0511, "lng": 9.7679, "accuracy": 5 }]'
            />
            <button onClick={handleProcess} disabled={processing} className="btn-accent w-full mt-3 brand-surface-analytics">
              {processing ? <><Spinner size="sm" /> <span>Processing…</span></> : <><Icon.Play className="w-4 h-4" /> <span>Run pipeline</span></>}
            </button>
          </div>

          <div>
            <label className="label">Pipeline result</label>
            {error && <Alert tone="danger">{error}</Alert>}
            {!result && !error && (
              <div className="input resize-none h-[320px] flex items-center justify-center text-ink-400 text-sm">
                Results will appear here
              </div>
            )}
            {result && (
              <div className="space-y-3">
                {/* Summary */}
                <div className={`p-3.5 rounded-lg border ${result.ok ? 'bg-success-50 dark:bg-success-500/10 border-success-500/20' : 'bg-danger-50 dark:bg-danger-500/10 border-danger-500/20'}`}>
                  <p className={`text-sm font-semibold ${result.ok ? 'text-success-700 dark:text-success-500' : 'text-danger-700 dark:text-danger-500'}`}>
                    {result.ok ? '✓ Valid polygon' : '✕ Validation issues'}
                  </p>
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1 nums">
                    {result.output.pointCount} pts · {result.output.areaHectares.toFixed(4)} ha · {result.output.perimeterMeters.toFixed(1)} m · {result.output.reductionPct}% reduction
                  </p>
                </div>

                {/* Validation issues */}
                {result.validation.issues.length > 0 && (
                  <div className="space-y-1">
                    {result.validation.issues.map((issue, i) => (
                      <p key={i} className="text-2xs text-danger-700 dark:text-danger-500">• {issue}</p>
                    ))}
                  </div>
                )}

                {/* Stage reports */}
                <div className="space-y-1.5">
                  <p className="text-2xs uppercase tracking-micro text-ink-500 dark:text-ink-400">Stage reports</p>
                  {result.stages.map((s, i) => (
                    <div key={i} className="flex items-center justify-between p-2 surface-inset rounded text-2xs">
                      <span className="font-mono text-ink-700 dark:text-ink-200">{s.name}</span>
                      <span className="nums text-ink-500 dark:text-ink-400">
                        {s.inputCount} → {s.outputCount} ({s.durationMs}ms)
                      </span>
                    </div>
                  ))}
                </div>

                {/* GeoJSON output */}
                <details>
                  <summary className="cursor-pointer text-2xs uppercase tracking-micro text-ink-500 dark:text-ink-400">GeoJSON output</summary>
                  <pre className="mt-2 p-2.5 surface-inset rounded text-2xs font-mono overflow-auto max-h-40">
                    {JSON.stringify(result.output.geojson, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   TEMPORAL EXPLORER TAB — farm version history
   ═══════════════════════════════════════════════════════════════ */
const TemporalExplorerTab: React.FC = () => {
  const [farmerId, setFarmerId] = useState('');
  const [versions, setVersions] = useState<FarmVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [farmers, setFarmers] = useState<any[]>([]);
  const versionsPag = usePagination(versions, 10);

  useEffect(() => {
    apiClient.get('/farmers').then(res => {
      const data = Array.isArray(res.data) ? res.data : [];
      setFarmers(data);
      if (data.length > 0) setFarmerId(data[0].id);
    }).catch(() => {});
  }, []);

  const fetchHistory = useCallback(async () => {
    if (!farmerId) return;
    setLoading(true);
    try {
      const h = await spatialApi.getFarmHistory(farmerId, 100);
      setVersions(h);
    } catch { setVersions([]); }
    setSearched(true);
    setLoading(false);
  }, [farmerId]);

  useEffect(() => {
    if (farmerId) fetchHistory();
  }, [farmerId, fetchHistory]);

  return (
    <div className="space-y-6">
      <Card padding="lg">
        <SectionHeader
          eyebrow="Time travel"
          title="Temporal version explorer"
          subtitle="Every farm boundary change is snapshotted. Replay farm state at any point in time."
          icon={<Icon.Clock className="w-4 h-4" />}
        />
        <div className="mt-4">
          <label className="label">Select farmer</label>
          <select value={farmerId} onChange={(e) => setFarmerId(e.target.value)} className="input">
            {farmers.map(f => <option key={f.id} value={f.id}>{f.name} · {f.village || 'Unknown'}</option>)}
          </select>
        </div>
      </Card>

      {loading ? (
        <Card padding="lg"><div className="flex justify-center py-8"><Spinner /></div></Card>
      ) : searched && versions.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={<Icon.Clock className="w-5 h-5" />}
            title="No version history yet"
            description="Boundary snapshots appear here automatically when farm polygons are updated (via Spatial OS event subscribers)."
          />
        </Card>
      ) : versions.length > 0 ? (
        <Card padding="none" className="overflow-hidden">
          <div className="p-5 border-b border-ink-200 dark:border-ink-800">
            <SectionHeader eyebrow="Timeline" title={`Version history (${versionsPag.total})`} icon={<Icon.Clock className="w-4 h-4" />} />
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Version</th><th>Captured at</th><th>Trigger event</th><th>Area (ha)</th><th>Village</th><th>Notes</th></tr></thead>
              <tbody>
                {versionsPag.paginatedItems.map((v) => (
                  <tr key={v.id}>
                    <td><span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-ink-100 dark:bg-ink-800 text-xs font-semibold nums text-ink-700 dark:text-ink-200">v{v.versionNumber}</span></td>
                    <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{new Date(v.capturedAt).toLocaleString()}</td>
                    <td>{v.triggerEvent ? <StatusPill tone="info" dot>{v.triggerEvent.replace(/_/g, ' ')}</StatusPill> : <span className="text-2xs text-ink-400">—</span>}</td>
                    <td className="nums">{v.polygonAreaHectares ? v.polygonAreaHectares.toFixed(4) : '—'}</td>
                    <td className="text-2xs text-ink-500 dark:text-ink-400">{v.village || '—'}</td>
                    <td className="text-2xs text-ink-500 dark:text-ink-400 truncate max-w-xs">{v.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-ink-200 dark:border-ink-800">
            <Pagination page={versionsPag.page} pageSize={versionsPag.pageSize} total={versionsPag.total} onPageChange={versionsPag.setPage} />
          </div>
        </Card>
      ) : null}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   DIGITAL TWIN TAB — maturity levels + crop/weather recorders
   ═══════════════════════════════════════════════════════════════ */
const DigitalTwinTab: React.FC = () => {
  const [farmerId, setFarmerId] = useState('');
  const [farmers, setFarmers] = useState<any[]>([]);
  const [profile, setProfile] = useState<DigitalTwinProfile | null>(null);
  const [features, setFeatures] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [showCropForm, setShowCropForm] = useState(false);
  const [showWeatherForm, setShowWeatherForm] = useState(false);

  useEffect(() => {
    apiClient.get('/farmers').then(res => {
      const data = Array.isArray(res.data) ? res.data : [];
      setFarmers(data);
      if (data.length > 0) setFarmerId(data[0].id);
    }).catch(() => {});
  }, []);

  const fetchProfile = useCallback(async () => {
    if (!farmerId) return;
    setLoading(true);
    try {
      const [p, f] = await Promise.all([
        spatialApi.getTwinProfile(farmerId).catch(() => null),
        spatialApi.getFeatures(farmerId).catch(() => ({})),
      ]);
      setProfile(p);
      setFeatures(f);
    } catch {}
    setLoading(false);
  }, [farmerId]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  return (
    <div className="space-y-6">
      <Card padding="lg">
        <SectionHeader
          eyebrow="Maturity"
          title="Digital twin profile"
          subtitle="Each farm progresses through 8 maturity levels, from raw boundary to predictive AI."
          icon={<Icon.Cpu className="w-4 h-4" />}
        />
        <div className="mt-4">
          <label className="label">Select farmer</label>
          <select value={farmerId} onChange={(e) => setFarmerId(e.target.value)} className="input">
            {farmers.map(f => <option key={f.id} value={f.id}>{f.name} · {f.village || 'Unknown'}</option>)}
          </select>
        </div>
      </Card>

      {loading ? (
        <Card padding="lg"><div className="flex justify-center py-8"><Spinner /></div></Card>
      ) : profile ? (
        <>
          {/* Maturity ladder */}
          <Card padding="lg">
            <SectionHeader eyebrow="Progression" title={`Level ${profile.currentLevel}: ${TWIN_LEVELS[profile.currentLevel].label}`} icon={<Icon.Sparkles className="w-4 h-4" />} />
            <div className="mt-5 flex items-center justify-between gap-1 overflow-x-auto no-scrollbar pb-2">
              {TWIN_LEVELS.map((lvl, idx) => {
                const reached = idx <= profile.currentLevel;
                const isCurrent = idx === profile.currentLevel;
                return (
                  <div key={lvl.level} className="flex flex-col items-center gap-2 shrink-0 min-w-[80px]">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-semibold transition-all ${
                      isCurrent ? 'bg-analytics-600 text-white shadow-e3 scale-110'
                      : reached ? 'bg-analytics-100 dark:bg-analytics-500/20 text-analytics-700 dark:text-analytics-500'
                      : 'bg-ink-100 dark:bg-ink-800 text-ink-400 dark:text-ink-500'
                    }`}>
                      {lvl.icon}
                    </div>
                    <p className={`text-2xs font-medium text-center ${isCurrent ? 'text-ink-900 dark:text-ink-50' : reached ? 'text-ink-600 dark:text-ink-300' : 'text-ink-400 dark:text-ink-500'}`}>
                      L{lvl.level}
                    </p>
                    <p className="text-2xs text-ink-500 dark:text-ink-400 text-center">{lvl.label}</p>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Capability flags */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricTile label="Boundary"    value={profile.hasBoundary ? 'Captured' : 'Missing'}    icon={<Icon.MapPin className="w-4 h-4" />} accent={profile.hasBoundary ? 'analytics' : undefined} />
            <MetricTile label="Crops"       value={profile.hasCrops ? 'Recorded' : 'None'}           icon={<Icon.Leaf className="w-4 h-4" />} />
            <MetricTile label="Weather"     value={profile.hasWeather ? 'Streaming' : 'None'}        icon={<Icon.Cloud className="w-4 h-4" />} />
            <MetricTile label="Satellite"   value={profile.hasSatellite ? 'Active' : 'Pending'}      icon={<Icon.Database className="w-4 h-4" />} />
          </div>

          {/* Level history */}
          {profile.levelHistory.length > 0 && (
            <Card padding="none" className="overflow-hidden">
              <div className="p-5 border-b border-ink-200 dark:border-ink-800">
                <SectionHeader eyebrow="Audit trail" title="Level changes" icon={<Icon.Clock className="w-4 h-4" />} />
              </div>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr><th>Level</th><th>Changed at</th><th>Reason</th></tr></thead>
                  <tbody>
                    {profile.levelHistory.map((h, i) => (
                      <tr key={i}>
                        <td><StatusPill tone="info" dot>L{h.level} · {TWIN_LEVELS[h.level]?.label || 'Unknown'}</StatusPill></td>
                        <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{new Date(h.changedAt).toLocaleString()}</td>
                        <td className="text-2xs text-ink-500 dark:text-ink-400">{h.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Feature store */}
          {Object.keys(features).length > 0 && (
            <Card padding="none" className="overflow-hidden">
              <div className="p-5 border-b border-ink-200 dark:border-ink-800">
                <SectionHeader eyebrow="ML-ready" title="Feature store (latest values)" icon={<Icon.Database className="w-4 h-4" />} />
              </div>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr><th>Feature</th><th>Value</th></tr></thead>
                  <tbody>
                    {Object.entries(features).map(([k, v]) => (
                      <tr key={k}>
                        <td className="font-mono text-2xs text-ink-700 dark:text-ink-200">{k}</td>
                        <td className="nums font-medium text-ink-900 dark:text-ink-50">{String(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowCropForm(true)} className="btn-primary">
              <Icon.Plus className="w-4 h-4" /> Record crop (L2)
            </button>
            <button onClick={() => setShowWeatherForm(true)} className="btn-secondary">
              <Icon.Cloud className="w-4 h-4" /> Record weather (L3)
            </button>
          </div>
        </>
      ) : (
        <Card padding="lg">
          <EmptyState icon={<Icon.Cpu className="w-5 h-5" />} title="No twin profile" description="Select a farmer to view their digital twin maturity." />
        </Card>
      )}

      {/* Crop form modal */}
      {showCropForm && farmerId && (
        <CropFormModal
          farmerId={farmerId}
          onClose={() => setShowCropForm(false)}
          onSaved={() => { setShowCropForm(false); fetchProfile(); }}
        />
      )}
      {showWeatherForm && farmerId && (
        <WeatherFormModal
          farmerId={farmerId}
          onClose={() => setShowWeatherForm(false)}
          onSaved={() => { setShowWeatherForm(false); fetchProfile(); }}
        />
      )}
    </div>
  );
};

/* ─── Crop form modal (Level 2) ─── */
const CropFormModal: React.FC<{ farmerId: string; onClose: () => void; onSaved: () => void }> = ({ farmerId, onClose, onSaved }) => {
  const [form, setForm] = useState({ cropName: 'Cocoa', variety: '', areaHectares: '', plantedAt: '', expectedYieldKg: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await spatialApi.recordCrop(farmerId, {
        cropName: form.cropName,
        variety: form.variety || undefined,
        areaHectares: form.areaHectares ? parseFloat(form.areaHectares) : undefined,
        plantedAt: form.plantedAt || undefined,
        expectedYieldKg: form.expectedYieldKg ? parseFloat(form.expectedYieldKg) : undefined,
        notes: form.notes || undefined,
      });
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to record crop');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <div className="sheet-backdrop hidden md:block" onClick={onClose} />
      <div className="hidden md:flex fixed inset-0 z-50 items-center justify-center p-6 pointer-events-none">
        <Card padding="lg" className="max-w-md w-full pointer-events-auto animate-scale-in">
          <ModalBody title="Record crop (Digital Twin L2)" onClose={onClose} onSubmit={handleSubmit} submitting={submitting} error={error}>
            <div className="space-y-3">
              <div><label className="label">Crop name *</label><input className="input" required value={form.cropName} onChange={e => setForm({...form, cropName: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Variety</label><input className="input" value={form.variety} onChange={e => setForm({...form, variety: e.target.value})} /></div>
                <div><label className="label">Area (ha)</label><input type="number" step="0.01" className="input" value={form.areaHectares} onChange={e => setForm({...form, areaHectares: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Planted date</label><input type="date" className="input" value={form.plantedAt} onChange={e => setForm({...form, plantedAt: e.target.value})} /></div>
                <div><label className="label">Expected yield (kg)</label><input type="number" className="input" value={form.expectedYieldKg} onChange={e => setForm({...form, expectedYieldKg: e.target.value})} /></div>
              </div>
              <div><label className="label">Notes</label><textarea className="input resize-none" rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
            </div>
          </ModalBody>
        </Card>
      </div>
      <div className="md:hidden">
        <div className="sheet-backdrop" onClick={onClose} />
        <div className="sheet animate-sheet-up">
          <div className="sheet-handle" />
          <div className="px-5 pb-4 pt-2 flex items-center justify-between">
            <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">Record crop</h3>
            <IconButton label="Close" onClick={onClose} size="sm"><Icon.Plus className="w-4 h-4 rotate-45" /></IconButton>
          </div>
          <div className="px-5 pb-6">
            <ModalBody title="" onClose={onClose} onSubmit={handleSubmit} submitting={submitting} error={error} embedded>
              <div className="space-y-3">
                <div><label className="label">Crop name *</label><input className="input" required value={form.cropName} onChange={e => setForm({...form, cropName: e.target.value})} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label">Variety</label><input className="input" value={form.variety} onChange={e => setForm({...form, variety: e.target.value})} /></div>
                  <div><label className="label">Area (ha)</label><input type="number" step="0.01" className="input" value={form.areaHectares} onChange={e => setForm({...form, areaHectares: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label">Planted date</label><input type="date" className="input" value={form.plantedAt} onChange={e => setForm({...form, plantedAt: e.target.value})} /></div>
                  <div><label className="label">Expected yield (kg)</label><input type="number" className="input" value={form.expectedYieldKg} onChange={e => setForm({...form, expectedYieldKg: e.target.value})} /></div>
                </div>
                <div><label className="label">Notes</label><textarea className="input resize-none" rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
              </div>
            </ModalBody>
          </div>
        </div>
      </div>
    </>
  );
};

/* ─── Weather form modal (Level 3) ─── */
const WeatherFormModal: React.FC<{ farmerId: string; onClose: () => void; onSaved: () => void }> = ({ farmerId, onClose, onSaved }) => {
  const [form, setForm] = useState({ lat: '', lng: '', temperatureC: '', humidityPct: '', rainfallMm: '', source: 'manual' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await spatialApi.recordWeather(farmerId, {
        lat: parseFloat(form.lat), lng: parseFloat(form.lng),
        temperatureC: form.temperatureC ? parseFloat(form.temperatureC) : undefined,
        humidityPct: form.humidityPct ? parseFloat(form.humidityPct) : undefined,
        rainfallMm: form.rainfallMm ? parseFloat(form.rainfallMm) : undefined,
        source: form.source,
      });
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to record weather');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <div className="sheet-backdrop hidden md:block" onClick={onClose} />
      <div className="hidden md:flex fixed inset-0 z-50 items-center justify-center p-6 pointer-events-none">
        <Card padding="lg" className="max-w-md w-full pointer-events-auto animate-scale-in">
          <ModalBody title="Record weather (Digital Twin L3)" onClose={onClose} onSubmit={handleSubmit} submitting={submitting} error={error}>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Latitude *</label><input type="number" step="0.0001" required className="input" value={form.lat} onChange={e => setForm({...form, lat: e.target.value})} /></div>
                <div><label className="label">Longitude *</label><input type="number" step="0.0001" required className="input" value={form.lng} onChange={e => setForm({...form, lng: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="label">Temp (°C)</label><input type="number" step="0.1" className="input" value={form.temperatureC} onChange={e => setForm({...form, temperatureC: e.target.value})} /></div>
                <div><label className="label">Humidity (%)</label><input type="number" step="0.1" className="input" value={form.humidityPct} onChange={e => setForm({...form, humidityPct: e.target.value})} /></div>
                <div><label className="label">Rain (mm)</label><input type="number" step="0.1" className="input" value={form.rainfallMm} onChange={e => setForm({...form, rainfallMm: e.target.value})} /></div>
              </div>
              <div><label className="label">Source</label>
                <select className="input" value={form.source} onChange={e => setForm({...form, source: e.target.value})}>
                  <option value="manual">Manual observation</option>
                  <option value="openmeteo">Open-Meteo API</option>
                  <option value="iot">IoT sensor</option>
                </select>
              </div>
            </div>
          </ModalBody>
        </Card>
      </div>
      <div className="md:hidden">
        <div className="sheet-backdrop" onClick={onClose} />
        <div className="sheet animate-sheet-up">
          <div className="sheet-handle" />
          <div className="px-5 pb-4 pt-2 flex items-center justify-between">
            <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">Record weather</h3>
            <IconButton label="Close" onClick={onClose} size="sm"><Icon.Plus className="w-4 h-4 rotate-45" /></IconButton>
          </div>
          <div className="px-5 pb-6">
            <ModalBody title="" onClose={onClose} onSubmit={handleSubmit} submitting={submitting} error={error} embedded>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label">Latitude *</label><input type="number" step="0.0001" required className="input" value={form.lat} onChange={e => setForm({...form, lat: e.target.value})} /></div>
                  <div><label className="label">Longitude *</label><input type="number" step="0.0001" required className="input" value={form.lng} onChange={e => setForm({...form, lng: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="label">Temp (°C)</label><input type="number" step="0.1" className="input" value={form.temperatureC} onChange={e => setForm({...form, temperatureC: e.target.value})} /></div>
                  <div><label className="label">Humidity (%)</label><input type="number" step="0.1" className="input" value={form.humidityPct} onChange={e => setForm({...form, humidityPct: e.target.value})} /></div>
                  <div><label className="label">Rain (mm)</label><input type="number" step="0.1" className="input" value={form.rainfallMm} onChange={e => setForm({...form, rainfallMm: e.target.value})} /></div>
                </div>
                <div><label className="label">Source</label>
                  <select className="input" value={form.source} onChange={e => setForm({...form, source: e.target.value})}>
                    <option value="manual">Manual observation</option>
                    <option value="openmeteo">Open-Meteo API</option>
                    <option value="iot">IoT sensor</option>
                  </select>
                </div>
              </div>
            </ModalBody>
          </div>
        </div>
      </div>
    </>
  );
};

/* ─── Shared modal body ─── */
const ModalBody: React.FC<{
  title: string; onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  submitting: boolean; error: string | null;
  children: React.ReactNode; embedded?: boolean;
}> = ({ title, onClose, onSubmit, submitting, error, children, embedded }) => {
  if (embedded) {
    return (
      <form onSubmit={onSubmit} className="space-y-3">
        {children}
        {error && <Alert tone="danger">{error}</Alert>}
        <button type="submit" disabled={submitting} className="btn-accent w-full py-3 brand-surface-analytics">
          {submitting ? <><Spinner size="sm" /> <span>Saving…</span></> : 'Save'}
        </button>
      </form>
    );
  }
  return (
    <>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">{title}</h3>
        <IconButton label="Close" onClick={onClose} size="sm"><Icon.Plus className="w-4 h-4 rotate-45" /></IconButton>
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        {children}
        {error && <Alert tone="danger">{error}</Alert>}
        <button type="submit" disabled={submitting} className="btn-accent w-full py-3 brand-surface-analytics">
          {submitting ? <><Spinner size="sm" /> <span>Saving…</span></> : 'Save'}
        </button>
      </form>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * Spatial Analytics Tab — heatmaps, clusters, density, spatial joins
 * ═══════════════════════════════════════════════════════════════ */
const SpatialAnalyticsTab: React.FC = () => {
  const [heatmapLayer, setHeatmapLayer] = useState('disease_risk');
  const [heatmap, setHeatmap] = useState<any>(null);
  const [density, setDensity] = useState<any[]>([]);
  const [layers, setLayers] = useState<any[]>([]);
  const [nearbyFarms, setNearbyFarms] = useState<any[]>([]);
  const [nearLat, setNearLat] = useState('');
  const [nearLng, setNearLng] = useState('');
  const [nearRadius, setNearRadius] = useState('5000');
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [hmRes, densRes, layersRes] = await Promise.all([
        apiClient.get(`/spatial/heatmap/${heatmapLayer}`).catch(() => ({ data: { points: [] } })),
        apiClient.get('/spatial/density').catch(() => ({ data: { density: [] } })),
        apiClient.get('/spatial/layers').catch(() => ({ data: { layers: [] } })),
      ]);
      setHeatmap(hmRes.data);
      setDensity(densRes.data?.density || []);
      setLayers(layersRes.data?.layers || []);
    } finally { setLoading(false); }
  }, [heatmapLayer]);

  useEffect(() => { refresh(); }, [refresh]);

  const searchNearby = async () => {
    if (!nearLat || !nearLng) return;
    const res = await apiClient.get(`/spatial/farms-near?lat=${nearLat}&lng=${nearLng}&radius_m=${nearRadius}`);
    setNearbyFarms(res.data?.farms || []);
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-6">
      {/* Heatmap */}
      <Card padding="lg">
        <SectionHeader eyebrow="Spatial intensity" title="Heatmap" subtitle="Per-farm intensity points for visual overlay on maps" icon={<Icon.Layers className="w-4 h-4" />} />
        <div className="mt-3 flex gap-2 flex-wrap">
          {['disease_risk', 'farm_density', 'truck_density', 'yield'].map(l => (
            <button key={l} onClick={() => setHeatmapLayer(l)}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium ${heatmapLayer === l ? 'bg-admin-500 text-white' : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300'}`}>
              {l.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
        {heatmap?.points && heatmap.points.length > 0 ? (
          <div className="mt-4">
            <p className="text-sm text-ink-600 dark:text-ink-300 mb-2">{heatmap.points.length} intensity points {heatmap.cached ? '(cached)' : ''}</p>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {heatmap.points.slice(0, 30).map((p: any, i: number) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-md surface-inset text-xs">
                  <span className="text-ink-700 dark:text-ink-200">{p.metadata?.name || `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-2 rounded-full bg-ink-200 dark:bg-ink-700 overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${p.intensity * 100}%`, background: p.intensity > 0.7 ? '#dc2626' : p.intensity > 0.4 ? '#f59e0b' : '#10b981' }} />
                    </div>
                    <span className="nums text-ink-500 dark:text-ink-400">{(p.intensity * 100).toFixed(0)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="mt-4 text-sm text-ink-500 dark:text-ink-400">No data for this heatmap layer yet.</p>
        )}
      </Card>

      {/* Density per village */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Spatial distribution" title={`Farm density per village (${density.length})`} icon={<Icon.MapPin className="w-4 h-4" />} />
        </div>
        {density.length === 0 ? (
          <EmptyState icon={<Icon.MapPin className="w-5 h-5" />} title="No density data" description="Map farms first to see density analysis." />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Village</th><th>Farms</th><th>Farmers</th><th>Total area (ha)</th><th>Avg farm size (ha)</th><th>EUDR verified</th></tr></thead>
              <tbody>
                {density.slice(0, 30).map((d: any, i: number) => (
                  <tr key={i}>
                    <td className="text-sm font-medium text-ink-900 dark:text-ink-50">{d.village}</td>
                    <td className="nums">{d.farm_count}</td>
                    <td className="nums">{d.farmer_count}</td>
                    <td className="nums">{Number(d.total_area_ha).toFixed(2)}</td>
                    <td className="nums">{Number(d.avg_farm_size_ha).toFixed(2)}</td>
                    <td className="nums text-success-700 dark:text-success-500">{d.eudr_verified}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Spatial join: farms near point */}
      <Card padding="lg">
        <SectionHeader eyebrow="Spatial query" title="Farms near a point" subtitle="Find all farms within X meters of a GPS coordinate" icon={<Icon.MapPin className="w-4 h-4" />} />
        <div className="mt-3 flex gap-2 flex-wrap">
          <input className="input flex-1 min-w-[120px]" type="number" step="0.0001" placeholder="Latitude" value={nearLat} onChange={e => setNearLat(e.target.value)} />
          <input className="input flex-1 min-w-[120px]" type="number" step="0.0001" placeholder="Longitude" value={nearLng} onChange={e => setNearLng(e.target.value)} />
          <input className="input w-32" type="number" placeholder="Radius (m)" value={nearRadius} onChange={e => setNearRadius(e.target.value)} />
          <button onClick={searchNearby} className="btn-primary text-xs"><Icon.MapPin className="w-3.5 h-3.5" /> Search</button>
        </div>
        {nearbyFarms.length > 0 && (
          <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
            {nearbyFarms.map((f: any, i: number) => (
              <div key={i} className="flex items-center justify-between p-2 rounded-md surface-inset text-xs">
                <span className="text-ink-700 dark:text-ink-200">{f.farm_name} — {f.farmer_name} ({f.village})</span>
                <span className="nums text-ink-500 dark:text-ink-400">{Number(f.distance_m).toFixed(0)} m</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Registered layers */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Registry" title={`Spatial layers (${layers.length})`} icon={<Icon.Layers className="w-4 h-4" />} />
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Layer</th><th>Type</th><th>Category</th><th>Source</th><th>Visible</th></tr></thead>
            <tbody>
              {layers.map((l: any, i: number) => (
                <tr key={i}>
                  <td className="text-sm font-medium text-ink-900 dark:text-ink-50">{l.display_name}</td>
                  <td><StatusPill tone="info" dot>{l.layer_type}</StatusPill></td>
                  <td className="text-sm">{l.category}</td>
                  <td className="text-2xs font-mono text-ink-500 dark:text-ink-400">{l.source_table}</td>
                  <td>{l.is_visible_by_default ? <StatusPill tone="success" dot>Default</StatusPill> : <StatusPill tone="info" dot>Hidden</StatusPill>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * Geofences Tab — virtual perimeters + enter/exit event log
 * ═══════════════════════════════════════════════════════════════ */
const GeofencesTab: React.FC = () => {
  const [geofences, setGeofences] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [gfRes, evRes] = await Promise.all([
        apiClient.get('/spatial/geofences').catch(() => ({ data: { geofences: [] } })),
        apiClient.get('/spatial/geofences/events?limit=50').catch(() => ({ data: { events: [] } })),
      ]);
      setGeofences(gfRes.data?.geofences || []);
      setEvents(evRes.data?.events || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-6">
      {/* Geofences list */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Virtual perimeters" title={`Geofences (${geofences.length})`} subtitle="Auto-created per farm + custom perimeters for dispatches, restricted zones, collection points" icon={<Icon.MapPin className="w-4 h-4" />} />
        </div>
        {geofences.length === 0 ? (
          <EmptyState icon={<Icon.MapPin className="w-5 h-5" />} title="No geofences yet" description="Map farms to auto-create farm boundary geofences." />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Type</th><th>Centroid</th><th>Events (24h)</th><th>Enters</th><th>Exits</th></tr></thead>
              <tbody>
                {geofences.map((g: any, i: number) => (
                  <tr key={i}>
                    <td className="text-sm font-medium text-ink-900 dark:text-ink-50">{g.name}</td>
                    <td><StatusPill tone={g.geofence_type === 'farm' ? 'success' : 'info'} dot>{g.geofence_type}</StatusPill></td>
                    <td className="text-2xs font-mono text-ink-500 dark:text-ink-400 nums">
                      {g.centroid_lat ? `${Number(g.centroid_lat).toFixed(4)}, ${Number(g.centroid_lng).toFixed(4)}` : '—'}
                    </td>
                    <td className="nums">{g.events_24h || 0}</td>
                    <td className="nums text-success-700 dark:text-success-500">{g.enters_24h || 0}</td>
                    <td className="nums text-warning-700 dark:text-warning-500">{g.exits_24h || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Recent geofence events */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Live tracking" title="Recent geofence events" icon={<Icon.Clock className="w-4 h-4" />} />
        </div>
        {events.length === 0 ? (
          <EmptyState icon={<Icon.Clock className="w-5 h-5" />} title="No geofence events" description="When trucks or agents cross geofence boundaries, events will appear here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Time</th><th>Event</th><th>Entity</th><th>Geofence</th><th>Location</th></tr></thead>
              <tbody>
                {events.map((e: any, i: number) => (
                  <tr key={i}>
                    <td className="text-2xs text-ink-500 dark:text-ink-400">{new Date(e.occurred_at).toLocaleString()}</td>
                    <td><StatusPill tone={e.event === 'enter' ? 'success' : e.event === 'exit' ? 'warning' : 'info'} dot>{e.event}</StatusPill></td>
                    <td className="text-sm">{e.entity_type}</td>
                    <td className="text-sm">{e.geofence_name}</td>
                    <td className="text-2xs font-mono text-ink-500 dark:text-ink-400 nums">
                      {e.location_lat ? `${Number(e.location_lat).toFixed(4)}, ${Number(e.location_lng).toFixed(4)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * AI Insights Tab — Multi-model AI spatial intelligence (v3.2)
 * ═══════════════════════════════════════════════════════════════ */
const AiInsightsTab: React.FC = () => {
  const [insights, setInsights] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/spatial/ai-insights?limit=20');
      setInsights(res.data?.insights || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleGenerate = async () => {
    setGenerating(true); setError(null);
    try {
      await apiClient.post('/spatial/ai-insights/generate', {});
      await refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const handleAcknowledge = async (id: string) => {
    try {
      await apiClient.post(`/spatial/ai-insights/${id}/acknowledge`);
      refresh();
    } catch { /* non-fatal */ }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}

      <Card padding="md">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="section-eyebrow">Multi-model AI spatial intelligence (admin-configured)</p>
            <p className="text-sm text-ink-700 dark:text-ink-300">AI analyzes all spatial data to detect disease hotspots, predict yields, and identify anomalies.</p>
          </div>
          <button onClick={handleGenerate} disabled={generating} className="btn-accent brand-surface-admin text-sm">
            {generating ? <Spinner size="sm" /> : <Icon.Sparkles className="w-4 h-4" />} Generate insights
          </button>
        </div>
      </Card>

      {insights.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={<Icon.Sparkles className="w-5 h-5" />}
            title="No AI insights yet"
            description="Click 'Generate insights' to have Gemini analyze all spatial data (farms, disease risk, weather, batches, geofence events) and produce disease hotspot + yield prediction insights."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {insights.map((ins: any, i: number) => (
            <Card key={i} padding="lg">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusPill tone={ins.severity === 'critical' ? 'danger' : ins.severity === 'warning' ? 'warning' : 'info'} dot>
                      {ins.severity}
                    </StatusPill>
                    <span className="text-2xs text-ink-500 dark:text-ink-400">{ins.insight_type.replace(/_/g, ' ')}</span>
                    {ins.region && <span className="text-2xs text-ink-500 dark:text-ink-400">· {ins.region}</span>}
                    <span className="text-2xs text-ink-500 dark:text-ink-400">· {Math.round(Number(ins.confidence || 0) * 100)}% confidence</span>
                  </div>
                  <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{ins.title}</p>
                </div>
                {!ins.is_acknowledged && (
                  <button onClick={() => handleAcknowledge(ins.id)} className="btn-secondary text-xs shrink-0">Acknowledge</button>
                )}
              </div>
              <p className="text-sm text-ink-700 dark:text-ink-300">{ins.summary}</p>
              {ins.details && (
                <details className="mt-2">
                  <summary className="text-2xs text-ink-500 dark:text-ink-400 cursor-pointer">Details</summary>
                  <pre className="mt-1 p-2 rounded surface-inset text-2xs overflow-x-auto font-mono">
                    {JSON.stringify(ins.details, null, 2)}
                  </pre>
                </details>
              )}
              <p className="mt-2 text-3xs text-ink-400 dark:text-ink-500">
                Generated: {new Date(ins.created_at).toLocaleString()} · Model: {ins.ai_model || 'gemini'}
                {ins.expires_at && ` · Expires: ${new Date(ins.expires_at).toLocaleDateString()}`}
              </p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SpatialIntelligence;
